import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import { useSharedDocument } from "~/api/v2/useSharedDocuments";
import { type SharedDocumentDetailsVersion } from "~/api/types";
import { Badge } from "~/atoms/badge";
import { Button } from "~/atoms/button";
import { Card, CardContent, CardHeader } from "~/atoms/card";
import { CenterSpinner } from "~/atoms/spinner";
import NoResult from "~/molecules/NoResult";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";
import { formatDate } from "~/helpers/date";
import { SharedDocumentPanel } from "~/organisms/SharedDocumentPanel";

const downloadUrl = (version: SharedDocumentDetailsVersion) =>
  `/api/v2/shared-documents/versions/${version.id}/download`;

const SharedDocumentPreview = ({
  version,
}: {
  version: SharedDocumentDetailsVersion;
}) => {
  const { t } = useTranslation();
  const isPdf = version.fileType === "pdf";
  const canEmbed = isPdf || version.hasPdfRendition;

  return (
    <Card className="flex flex-col gap-0">
      <CardHeader className="flex flex-row items-center justify-between border-b px-6">
        <span className="text-foreground text-base font-medium">
          {t(($) => $.commons.workflow.preview)}
        </span>
        <Button variant="outline" asChild>
          <a href={downloadUrl(version)} download>
            <Download />
            {t(($) => $.commons.buttons.downloadFile)}
          </a>
        </Button>
      </CardHeader>
      <CardContent className="h-[90vh] p-0">
        {canEmbed ? (
          <embed
            key={version.id}
            src={`${downloadUrl(version)}${isPdf ? "" : "?format=pdf"}`}
            className="size-full border-none"
            title="PDF Viewer"
          />
        ) : (
          <NoResult />
        )}
      </CardContent>
    </Card>
  );
};

export const SharedDocumentPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const documentId = id ? parseInt(id) : undefined;
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: doc, isLoading, mutate } = useSharedDocument(documentId);

  const versionIdFromUrl = searchParams.get("versionId");
  const versionId = versionIdFromUrl
    ? parseInt(versionIdFromUrl)
    : doc?.latestPublishedVersionId;
  const selectedVersion =
    doc?.versions.find((version) => version.id === versionId) ??
    doc?.versions[0];

  useEffect(() => {
    if (selectedVersion?.title) {
      document.title = `${selectedVersion.title} | North House`;
    }
  }, [selectedVersion?.title]);

  if (isLoading) {
    return <CenterSpinner withText />;
  }

  if (!doc || !selectedVersion) {
    return <NoResult message={t(($) => $.commons.noItems)} />;
  }

  return (
    <div className="container grid grid-rows-[min-content_1fr] py-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold text-wrap wrap-break-word">
            {selectedVersion.title}
          </h2>
          <Badge variant="delegation">
            {t(($) => $.sharedDocuments.sharedByGroup, {
              name: doc.sharedBy.name,
            })}
          </Badge>
        </div>

        <div className="w-64">
          <ControlledCombobox
            showSearch={false}
            options={doc.versions.map((version) => ({
              value: version.id.toString(),
              label: version.title,
              subtitle: `${t(($) => $.commons.documentStatus[version.status])} ${formatDate(version.publishedAt)}`,
            }))}
            value={selectedVersion.id.toString()}
            onChange={(value) => {
              const next = new URLSearchParams(searchParams);
              next.set("versionId", value);
              setSearchParams(next);
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 pt-2">
        <div className="col-span-3 xl:col-span-2">
          <SharedDocumentPreview version={selectedVersion} />
        </div>
        <div className="col-span-3 xl:col-span-1">
          <SharedDocumentPanel
            document={doc}
            version={selectedVersion}
            onChange={() => void mutate()}
          />
        </div>
      </div>
    </div>
  );
};
